import classNames from 'classnames';
import Business from '@material-ui/icons/Business';
import React from 'react';
import SummaryRow from 'components/SummaryRow/SummaryRow';
import { BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import DepartmentsSummary from 'views/Users/common/AddUserDialog/subcomponents/DepartmentsSummary';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/addUserDialogStyle';
import { makeStyles } from '@material-ui/core';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import moment from 'moment';
import { MAX_ADMIN_COUNT } from 'utils/const/users';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ErrorBox from 'components/ErrorBox/ErrorBox';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ISummaryTabProps {
	userName: string;
	emailAddress: string;
	selectedUserType: types.users.userType;
	accounts: SingleCorpAccount[];
	selectedDepartments: string[];
	departments: SingleDepartment[];
	permissionsTabHidden: boolean;
	selectedDepartment: SingleDepartment | null;
	selectedAccounts: string[];
	selectedBillingIds: string[];
	billingAccountTabHidden: boolean;
	expirationDate: moment.Moment | null;
	unlimitedExpirationDate: boolean;
	billingAccountExpirationDate: moment.Moment | null;
	billingAccountUnlimitedExpirationDate: boolean;
	assignedAccount: types.users.IAssignedAccount | null;
	contactNumber: string;
}
const SummaryTab = ({
	userName,
	emailAddress,
	selectedUserType,
	accounts,
	selectedDepartments,
	departments,
	permissionsTabHidden,
	selectedDepartment,
	selectedAccounts,
	selectedBillingIds,
	billingAccountTabHidden,
	expirationDate,
	unlimitedExpirationDate,
	billingAccountExpirationDate,
	billingAccountUnlimitedExpirationDate,
	assignedAccount,
	contactNumber,
}: ISummaryTabProps) => {
	const classes = useStyles();
	const allAccountsDisabled: boolean = accounts.every((account) => Number(account.adminsCount) >= MAX_ADMIN_COUNT);
	const showPermissionsLabel: boolean =
		selectedDepartments.length <= 0 &&
		(selectedUserType !== 'ADMIN' ||
			(selectedUserType === 'ADMIN' && !allAccountsDisabled && selectedAccounts.length > 0));

	const findDepartments = (): SingleDepartment[] => {
		return selectedDepartments
			.map((departmentId: string) => {
				return departments.find((department: SingleDepartment) => department.deptId === departmentId);
			})
			.filter(Boolean) as SingleDepartment[];
	};

	const findAccount = (id: string) => {
		return accounts.find((account) => account.accountId === id);
	};

	return (
		<div>
			<BodyS>{translate('user.details')}</BodyS>
			<SummaryRow label={translate('user.name')} value={userName} />
			<SummaryRow label={translate('email.address')} value={emailAddress} />
			<ConditionalRender
				show={selectedUserType !== 'ENDUSER'}
				onTrue={() => <SummaryRow label={translate('contact.number')} value={contactNumber} />}
			/>
			<SummaryRow label={translate('user.type')} value={selectedUserType} />
			<SummaryRow
				label={translate('organization.name')}
				value={!!assignedAccount ? assignedAccount.name : ''}
				className={classNames({
					[classes.smallMargin]: selectedDepartment,
					[classes.mediumMargin]: !selectedDepartment,
				})}
				withBorder={false}
			/>
			{selectedDepartment && (
				<SummaryRow
					label={translate('department')}
					value={selectedDepartment.name}
					className={classes.mediumMargin}
				/>
			)}
			{!permissionsTabHidden && (
				<>
					{showPermissionsLabel && <BodyS>{translate('permissions')}</BodyS>}
					{!allAccountsDisabled && selectedAccounts.length > 0 && selectedUserType !== 'MANAGER' && (
						<ExpirationDateSummaryRow
							expirationDate={expirationDate}
							unlimitedExpirationDate={unlimitedExpirationDate}
						/>
					)}
					{selectedUserType !== 'MANAGER' &&
						selectedAccounts.map((selectedAccount) => (
							<SummaryRow
								key={selectedAccount}
								label={translate('organization.name')}
								value={findAccount(selectedAccount)?.accountName}
								className={classes.smallMargin}
								withBorder={false}
								icon={<Business />}
							/>
						))}
					{selectedUserType === 'MANAGER' && selectedDepartments.length > 0 && (
						<DepartmentsSummary
							departments={findDepartments()}
							accounts={accounts}
							expirationDate={expirationDate}
							unlimitedExpirationDate={unlimitedExpirationDate}
						/>
					)}
					{selectedUserType === 'MANAGER' && selectedDepartments.length === 0 && (
						<ErrorBox text={translate('department.none.selected')}></ErrorBox>
					)}
				</>
			)}
			{!billingAccountTabHidden && (
				<>
					<br />
					<BodyS>{translate('billing.accounts')}</BodyS>
					<ConditionalRender
						show={selectedBillingIds.length > 0}
						onTrue={() => {
							return (
								<>
									<ExpirationDateSummaryRow
										expirationDate={billingAccountExpirationDate}
										unlimitedExpirationDate={billingAccountUnlimitedExpirationDate}
									/>
									<SummaryRow label={translate('billing.account')} value={selectedBillingIds[0]} />
									{selectedBillingIds.slice(1).map((selectedBillingId) => (
										<SummaryRow label={''} value={selectedBillingId} />
									))}
								</>
							);
						}}
						onFalse={() => <SummaryRow label={translate('none')} value={''} />}
					/>
				</>
			)}
		</div>
	);
};

export const ExpirationDateSummaryRow = ({
	expirationDate,
	unlimitedExpirationDate,
}: {
	expirationDate: moment.Moment | null;
	unlimitedExpirationDate: boolean;
}) => {
	return (
		<SummaryRow
			label={translate('access.expiration.date')}
			value={
				expirationDate && !unlimitedExpirationDate
					? expirationDate.format('YYYY-MM-DD')
					: translate('unlimited')
			}
		/>
	);
};

export default SummaryTab;
